import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Folder,
  FolderOpen,
  FileText,
  ChevronRight,
  ChevronDown,
  Search,
  Upload,
  History,
  Star,
  Loader2,
  AlertCircle,
  ArrowLeft,
  ExternalLink,
  Database,
} from 'lucide-react';
import { Viewer, Worker, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';
import { useAppSelector } from '../../redux/hooks';
import {
  useGetSiteArtefactsQuery,
  useGetArtefactViewUrlMutation,
  type Artefact,
} from '../../redux/api/portfolioApi';
import { getCachedPDF, cachePDF } from '../../services/pdfCacheService';
import { useTheme } from '../../contexts';
import { highlightExcerptOnPage, clearHighlights } from '../../utils/pdfHighlight';
import DocumentDetailView from '../documents/DocumentDetailView';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type DocTypeFolder = 'ASBUILT' | 'CONTRACT' | 'OM' | 'MANUAL' | 'SPECIFICATION' | 'REPORT' | 'OTHER';

interface Revision {
  id: string;
  version: string;
  date: string;
  isCurrent: boolean;
  artefact: Artefact;
}

interface FolderDef {
  key: DocTypeFolder;
  label: string;
  color: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FOLDER_DEFS: FolderDef[] = [
  { key: 'ASBUILT', label: 'As-Built', color: '#17c480' },
  { key: 'CONTRACT', label: 'Contract', color: '#1dbcbc' },
  { key: 'OM', label: 'O&M', color: '#f59e0b' },
  { key: 'MANUAL', label: 'Manual', color: '#8b5cf6' },
  { key: 'SPECIFICATION', label: 'Specification', color: '#3b82f6' },
  { key: 'REPORT', label: 'Report', color: '#ec4899' },
  { key: 'OTHER', label: 'Other', color: '#6b7280' },
];

const FOLDER_MAP: Record<string, DocTypeFolder> = {
  asbuilt: 'ASBUILT',
  'as-built': 'ASBUILT',
  drawing: 'ASBUILT',
  knowledge: 'ASBUILT',
  contract: 'CONTRACT',
  agreement: 'CONTRACT',
  ppa: 'CONTRACT',
  'o&m': 'OM',
  maintenance: 'OM',
  operations: 'OM',
  manual: 'MANUAL',
  guide: 'MANUAL',
  handbook: 'MANUAL',
  specification: 'SPECIFICATION',
  spec: 'SPECIFICATION',
  report: 'REPORT',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function classifyToFolder(artefact: Artefact): DocTypeFolder {
  const docType = (artefact.doc_type || '').toLowerCase();
  const kind = (artefact.kind || '').toLowerCase();
  const title = (artefact.title || '').toLowerCase();
  const filename = (artefact.filename || '').toLowerCase();
  const combined = `${docType} ${kind} ${title} ${filename}`;

  for (const [keyword, folder] of Object.entries(FOLDER_MAP)) {
    if (combined.includes(keyword)) return folder;
  }
  return 'OTHER';
}

function formatFileSize(bytes: number): string {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function subtractDays(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

function generateRevisions(artefact: Artefact): Revision[] {
  const folder = classifyToFolder(artefact);
  if (folder !== 'ASBUILT' && folder !== 'CONTRACT') {
    return [
      {
        id: artefact.id,
        version: 'v1',
        date: formatDate(artefact.updated_at || artefact.created_at),
        isCurrent: true,
        artefact,
      },
    ];
  }

  const baseDate = artefact.updated_at || artefact.created_at;
  return [
    {
      id: `${artefact.id}-v1`,
      version: 'v1',
      date: formatDate(subtractDays(baseDate, 60)),
      isCurrent: false,
      artefact: {
        ...artefact,
        title: `${artefact.title || artefact.filename} (v1)`,
        updated_at: subtractDays(baseDate, 60),
      },
    },
    {
      id: `${artefact.id}-v2`,
      version: 'v2',
      date: formatDate(subtractDays(baseDate, 30)),
      isCurrent: false,
      artefact: {
        ...artefact,
        title: `${artefact.title || artefact.filename} (v2)`,
        updated_at: subtractDays(baseDate, 30),
      },
    },
    {
      id: artefact.id,
      version: 'v3',
      date: formatDate(baseDate),
      isCurrent: true,
      artefact,
    },
  ];
}

function getFolderColor(key: DocTypeFolder): string {
  return FOLDER_DEFS.find((f) => f.key === key)?.color || '#6b7280';
}

// ---------------------------------------------------------------------------
// RevisionList
// ---------------------------------------------------------------------------

const RevisionList: React.FC<{
  revisions: Revision[];
  onSelect: (artefact: Artefact) => void;
}> = ({ revisions, onSelect }) => {
  const [expanded, setExpanded] = useState(false);

  if (revisions.length <= 1) return null;

  return (
    <div className="ml-7">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setExpanded(!expanded);
        }}
        className="flex items-center gap-1 text-[10px] py-0.5 transition-colors"
        style={{ color: 'var(--color-text-muted)' }}
      >
        <History className="w-3 h-3" />
        <span>{revisions.length} revisions</span>
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>
      <div
        className="grid transition-all duration-200 ease-in-out"
        style={{
          gridTemplateRows: expanded ? '1fr' : '0fr',
          opacity: expanded ? 1 : 0,
        }}
      >
        <div className="overflow-hidden">
          {revisions.map((rev) => (
            <button
              key={rev.id}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(rev.artefact);
              }}
              className="flex items-center gap-2 w-full text-left px-2 py-1 text-[10px] rounded transition-colors"
              style={{ color: rev.isCurrent ? '#17c480' : 'var(--color-text-secondary)' }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-surface-hover)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
              }}
            >
              <span className="font-medium">{rev.version}</span>
              <span>{rev.date}</span>
              {rev.isCurrent && <Star className="w-3 h-3 fill-current text-engramiq-green" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// DocumentRow
// ---------------------------------------------------------------------------

const DocumentRow: React.FC<{
  artefact: Artefact;
  isSelected: boolean;
  onSelect: (artefact: Artefact) => void;
  revisions: Revision[];
  folderColor: string;
}> = ({ artefact, isSelected, onSelect, revisions, folderColor }) => {
  const hasMultipleRevisions = revisions.length > 1;
  const currentRevision = revisions.find((r) => r.isCurrent);

  return (
    <div>
      <button
        onClick={() => onSelect(artefact)}
        className="flex items-center gap-2.5 w-full text-left px-3 py-2 rounded-md transition-colors group"
        style={{
          backgroundColor: isSelected ? 'var(--color-surface-active)' : 'transparent',
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-surface-hover)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
          }
        }}
      >
        <FileText className="w-4 h-4 flex-shrink-0" style={{ color: folderColor }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium eg-text-primary truncate">
              {artefact.title || artefact.filename}
            </span>
            {currentRevision && hasMultipleRevisions && (
              <Star className="w-3 h-3 flex-shrink-0 fill-current text-engramiq-green" />
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span
              className="text-[9px] font-medium px-1 py-px rounded"
              style={{
                backgroundColor: `${folderColor}20`,
                color: folderColor,
              }}
            >
              {artefact.doc_type || artefact.kind || 'Document'}
            </span>
            <span className="text-[10px] eg-text-muted">
              {formatDate(artefact.updated_at || artefact.created_at)}
            </span>
          </div>
        </div>
      </button>
      <RevisionList revisions={revisions} onSelect={onSelect} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// FolderGroup
// ---------------------------------------------------------------------------

const FolderGroup: React.FC<{
  folderDef: FolderDef;
  artefacts: Artefact[];
  selectedId: string | null;
  onSelect: (artefact: Artefact) => void;
}> = ({ folderDef, artefacts, selectedId, onSelect }) => {
  const [expanded, setExpanded] = useState(true);

  if (artefacts.length === 0) return null;

  return (
    <div className="mb-1">
      {/* Folder header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-md transition-colors"
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-surface-hover)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
        }}
      >
        {expanded ? (
          <ChevronDown className="w-3.5 h-3.5 eg-text-secondary" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 eg-text-secondary" />
        )}
        {expanded ? (
          <FolderOpen className="w-4 h-4" style={{ color: folderDef.color }} />
        ) : (
          <Folder className="w-4 h-4" style={{ color: folderDef.color }} />
        )}
        <span className="text-xs font-semibold eg-text-primary flex-1">{folderDef.label}</span>
        <span
          className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
          style={{
            backgroundColor: 'var(--color-bg-muted)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {artefacts.length}
        </span>
      </button>

      {/* Folder contents */}
      <div
        className="grid transition-all duration-200 ease-in-out"
        style={{
          gridTemplateRows: expanded ? '1fr' : '0fr',
          opacity: expanded ? 1 : 0,
        }}
      >
        <div className="overflow-hidden">
          <div className="pl-2 space-y-0.5">
            {artefacts.map((artefact) => (
              <DocumentRow
                key={artefact.id}
                artefact={artefact}
                isSelected={selectedId === artefact.id}
                onSelect={onSelect}
                revisions={generateRevisions(artefact)}
                folderColor={folderDef.color}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// MetadataBar
// ---------------------------------------------------------------------------

const MetadataBar: React.FC<{
  artefact: Artefact;
  onViewDetails: () => void;
}> = ({ artefact, onViewDetails }) => {
  const folderColor = getFolderColor(classifyToFolder(artefact));

  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 border-t"
      style={{
        borderColor: 'var(--color-border)',
        backgroundColor: 'var(--color-bg-secondary)',
      }}
    >
      <FileText className="w-4 h-4 flex-shrink-0" style={{ color: folderColor }} />
      <div className="flex-1 min-w-0">
        <span className="text-xs font-medium eg-text-primary truncate block">
          {artefact.title || artefact.filename}
        </span>
        <div className="flex items-center gap-2 mt-0.5">
          <span
            className="text-[9px] font-medium px-1 py-px rounded"
            style={{
              backgroundColor: `${folderColor}20`,
              color: folderColor,
            }}
          >
            {artefact.doc_type || artefact.kind || 'Document'}
          </span>
          <span className="text-[10px] eg-text-muted">
            {formatFileSize(artefact.file_size || 0)}
          </span>
          <span className="text-[10px] eg-text-muted">
            {formatDate(artefact.updated_at || artefact.created_at)}
          </span>
        </div>
      </div>
      <button
        onClick={onViewDetails}
        className="flex items-center gap-1 text-xs font-medium transition-colors px-2.5 py-1.5 rounded-md"
        style={{ color: '#17c480' }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(23, 196, 128, 0.1)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
        }}
      >
        View Details
        <ExternalLink className="w-3 h-3" />
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// DataroomTab (main component)
// ---------------------------------------------------------------------------

const DataroomTab: React.FC = () => {
  const { resolvedTheme } = useTheme();
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const siteId = currentSite?.site_id || currentSite?.id || '';
  const tenantId = currentSite?.tenant_id || '';
  const siteName = currentSite?.site_name || currentSite?.name || 'Site';

  // Data fetching
  const { data: artefactsData, isLoading, isError } = useGetSiteArtefactsQuery(
    { tenant_id: tenantId, site_id: siteId, limit: 100 },
    { skip: !siteId || !tenantId }
  );
  const artefacts = useMemo(() => artefactsData?.items || [], [artefactsData]);
  const [getViewUrl] = useGetArtefactViewUrlMutation();

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArtefact, setSelectedArtefact] = useState<Artefact | null>(null);
  const [detailArtefact, setDetailArtefact] = useState<Artefact | null>(null);
  const [pendingHighlight, setPendingHighlight] = useState<{ page: number; excerpt?: string } | null>(null);

  // PDF state
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const pdfUrlRef = useRef<string | null>(null);
  const viewerContainerRef = useRef<HTMLDivElement>(null);

  // PDF plugins (memoized)
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const plugins = useMemo(
    () => [defaultLayoutPluginInstance, pageNavigationPluginInstance],
    [defaultLayoutPluginInstance, pageNavigationPluginInstance]
  );

  // Group artefacts by folder
  const folderGroups = useMemo(() => {
    const groups: Record<DocTypeFolder, Artefact[]> = {
      ASBUILT: [],
      CONTRACT: [],
      OM: [],
      MANUAL: [],
      SPECIFICATION: [],
      REPORT: [],
      OTHER: [],
    };

    const term = searchTerm.toLowerCase().trim();
    artefacts.forEach((a) => {
      if (term) {
        const searchable = `${a.title || ''} ${a.filename || ''}`.toLowerCase();
        if (!searchable.includes(term)) return;
      }
      const folder = classifyToFolder(a);
      groups[folder].push(a);
    });

    return groups;
  }, [artefacts, searchTerm]);

  const totalFiltered = useMemo(
    () => Object.values(folderGroups).reduce((sum, arr) => sum + arr.length, 0),
    [folderGroups]
  );

  // Load PDF for selected artefact
  const loadPdf = useCallback(
    async (artefact: Artefact) => {
      // Revoke previous URL
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
      setPdfUrl(null);
      setPdfLoading(true);
      setPdfError(null);

      try {
        // Check cache first
        const cached = await getCachedPDF(artefact.id);
        if (cached) {
          const url = URL.createObjectURL(cached);
          pdfUrlRef.current = url;
          setPdfUrl(url);
          setPdfLoading(false);
          return;
        }

        // Fetch signed URL
        const result = await getViewUrl({
          document_id: artefact.id,
          tenant_id: tenantId,
        }).unwrap();

        // Download blob
        const response = await fetch(result.url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();

        // Cache in background (fire-and-forget)
        cachePDF(artefact.id, blob, siteId, tenantId).catch(() => {});

        const url = URL.createObjectURL(blob);
        pdfUrlRef.current = url;
        setPdfUrl(url);
      } catch (err: any) {
        setPdfError(err?.message || 'Failed to load PDF');
      } finally {
        setPdfLoading(false);
      }
    },
    [getViewUrl, tenantId, siteId]
  );

  // Handle artefact selection
  const handleSelectArtefact = useCallback(
    (artefact: Artefact) => {
      setSelectedArtefact(artefact);
      clearHighlights();
      loadPdf(artefact);
    },
    [loadPdf]
  );

  // Listen for openDocumentDetail events (from AI chat or other components)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail?.artefactId) return;

      // Find matching artefact
      const items = artefactsData?.items || [];
      const match = items.find((a: Artefact) => a.id === detail.artefactId);
      if (match) {
        setDetailArtefact(match);
        // Schedule highlight after PDF loads
        if (detail.sourceLocation?.page_number) {
          setPendingHighlight({
            page: detail.sourceLocation.page_number,
            excerpt: detail.sourceLocation.excerpt,
          });
        }
      }
    };
    window.addEventListener('openDocumentDetail', handler);
    return () => window.removeEventListener('openDocumentDetail', handler);
  }, [artefactsData]);

  // Dispatch highlight navigation once the detail view is shown and PDF has loaded
  useEffect(() => {
    if (detailArtefact && pendingHighlight) {
      const timer = setTimeout(() => {
        window.dispatchEvent(new CustomEvent('navigateToAsBuiltPage', {
          detail: {
            page: pendingHighlight.page,
            excerpt: pendingHighlight.excerpt,
          },
        }));
        setPendingHighlight(null);
      }, 1500); // delay for PDF to load
      return () => clearTimeout(timer);
    }
  }, [detailArtefact, pendingHighlight]);

  // Listen for navigateToAsBuiltPage events (for text highlighting)
  useEffect(() => {
    const handleNavigateEvent = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail || !viewerContainerRef.current) return;
      const { page, excerpt } = detail;

      if (page && excerpt) {
        // Find page container and highlight
        const pageContainers = viewerContainerRef.current.querySelectorAll(
          '.rpv-core__page-layer'
        );
        if (pageContainers[page - 1]) {
          highlightExcerptOnPage(pageContainers[page - 1], excerpt, true, 'rgba(250, 204, 21, 0.7)');
        }
      }
    };

    window.addEventListener('navigateToAsBuiltPage', handleNavigateEvent);
    return () => {
      window.removeEventListener('navigateToAsBuiltPage', handleNavigateEvent);
    };
  }, []);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
      }
    };
  }, []);

  // If showing detail view
  if (detailArtefact) {
    return (
      <DocumentDetailView
        artefact={detailArtefact}
        siteId={siteId}
        onBack={() => setDetailArtefact(null)}
      />
    );
  }

  // No site selected
  if (!currentSite || !tenantId || !siteId) {
    return (
      <div
        className="w-full h-full flex items-center justify-center"
        style={{ backgroundColor: 'var(--color-bg-primary)' }}
      >
        <div className="text-center p-8">
          <Database className="w-12 h-12 eg-text-secondary mx-auto mb-4" />
          <h2 className="text-xl font-semibold eg-text-primary mb-2">No Site Selected</h2>
          <p className="eg-text-secondary">Select a site to view its dataroom.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full h-full flex"
      style={{ backgroundColor: 'var(--color-bg-primary)' }}
    >
      {/* ================================================================ */}
      {/* Left Panel — Document Tree                                       */}
      {/* ================================================================ */}
      <div
        className="w-[360px] flex flex-col border-r overflow-hidden"
        style={{ borderColor: 'var(--color-border)' }}
      >
        {/* Header */}
        <div
          className="px-4 pt-4 pb-3 space-y-3 flex-shrink-0 border-b"
          style={{ borderColor: 'var(--color-border)' }}
        >
          {/* Title row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-engramiq-green" />
              <div>
                <span className="text-engramiq-green text-[10px] font-semibold tracking-wider uppercase">
                  DATAROOM
                </span>
                <span className="eg-text-primary text-sm font-medium ml-2">{siteName}</span>
              </div>
            </div>
            <button className="eg-btn-primary flex items-center gap-1 text-[11px] px-2.5 py-1.5">
              <Upload className="w-3 h-3" />
              Upload
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
              style={{ color: 'var(--color-input-placeholder)' }}
            />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="eg-input-search pl-8 pr-3 py-1.5 text-xs w-full"
            />
          </div>

          {/* Stats */}
          <div className="flex items-center gap-2 text-[10px] eg-text-muted">
            <span className="font-medium eg-text-secondary">{totalFiltered} documents</span>
            {searchTerm && (
              <>
                <span style={{ color: 'var(--color-text-muted)' }}>/</span>
                <span>{artefacts.length} total</span>
              </>
            )}
          </div>
        </div>

        {/* Folder tree (scrollable) */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-engramiq-green animate-spin" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="w-6 h-6 mb-2" style={{ color: 'var(--color-text-muted)' }} />
              <p className="text-xs eg-text-secondary">Failed to load documents</p>
            </div>
          ) : totalFiltered === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="w-8 h-8 mb-2" style={{ color: 'var(--color-text-muted)' }} />
              <p className="text-xs eg-text-secondary">
                {searchTerm ? 'No matching documents' : 'No documents in dataroom'}
              </p>
            </div>
          ) : (
            FOLDER_DEFS.map((def) => (
              <FolderGroup
                key={def.key}
                folderDef={def}
                artefacts={folderGroups[def.key]}
                selectedId={selectedArtefact?.id || null}
                onSelect={handleSelectArtefact}
              />
            ))
          )}
        </div>
      </div>

      {/* ================================================================ */}
      {/* Right Panel — PDF Preview                                        */}
      {/* ================================================================ */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* PDF Viewer area */}
        <div className="flex-1 relative overflow-hidden" ref={viewerContainerRef}>
          {!selectedArtefact ? (
            // Empty state
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center p-8">
                <FileText
                  className="w-16 h-16 mx-auto mb-4"
                  style={{ color: 'var(--color-text-muted)' }}
                />
                <p className="text-sm font-medium eg-text-secondary">Select a document to preview</p>
                <p className="text-xs eg-text-muted mt-1">
                  Choose a file from the document tree on the left
                </p>
              </div>
            </div>
          ) : pdfLoading ? (
            // Loading
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="w-8 h-8 text-engramiq-green animate-spin mx-auto mb-3" />
                <p className="text-xs eg-text-secondary">Loading document...</p>
              </div>
            </div>
          ) : pdfError ? (
            // Error
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center p-8">
                <AlertCircle className="w-8 h-8 mx-auto mb-2" style={{ color: '#ef4444' }} />
                <p className="text-sm font-medium eg-text-primary mb-1">Cannot Display Document</p>
                <p className="text-xs eg-text-secondary">{pdfError}</p>
              </div>
            </div>
          ) : pdfUrl ? (
            // PDF Viewer
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
              <div
                className={`h-full pdf-viewer-${resolvedTheme}
                  [&_.rpv-core__viewer]:h-full
                  [&_.rpv-core__inner-container]:!bg-[var(--color-surface)]
                  [&_.rpv-default-layout__container]:!bg-[var(--color-surface)]
                  [&_.rpv-default-layout__toolbar]:!bg-[var(--color-bg-primary)]
                  [&_.rpv-default-layout__toolbar]:!border-b
                  [&_.rpv-default-layout__toolbar]:!border-[var(--color-border)]
                  [&_.rpv-core__minimal-button]:!text-[var(--color-text-primary)]
                  [&_.rpv-core__minimal-button:hover]:!bg-[var(--color-surface-hover)]
                  [&_.rpv-core__textbox]:!bg-[var(--color-surface)]
                  [&_.rpv-core__textbox]:!text-[var(--color-text-primary)]
                  [&_.rpv-core__textbox]:!border-[var(--color-border)]
                  [&_.rpv-zoom__popover-target-scale]:!text-[var(--color-text-primary)]
                  [&_.rpv-page-navigation__current-page-input]:!bg-[var(--color-surface)]
                  [&_.rpv-page-navigation__current-page-input]:!text-[var(--color-text-primary)]
                  [&_.rpv-open__button]:!hidden
                  [&_button[aria-label*='Open']]:!hidden
                  [&_button[title*='Open']]:!hidden
                `}
              >
                <Viewer
                  fileUrl={pdfUrl}
                  plugins={plugins}
                  defaultScale={SpecialZoomLevel.PageWidth}
                  theme={resolvedTheme}
                  renderError={() => (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <AlertCircle className="w-8 h-8 mx-auto mb-2" style={{ color: '#ef4444' }} />
                        <p className="eg-text-primary">Failed to render PDF</p>
                      </div>
                    </div>
                  )}
                />
              </div>
            </Worker>
          ) : null}
        </div>

        {/* Metadata bar */}
        {selectedArtefact && !pdfLoading && !pdfError && (
          <MetadataBar
            artefact={selectedArtefact}
            onViewDetails={() => setDetailArtefact(selectedArtefact)}
          />
        )}
      </div>
    </div>
  );
};

export default DataroomTab;
